import { useEffect, useState, type FormEvent } from "react";
import {
  Plus,
  Search,
  Archive,
  ArrowUpRight,
  FileText,
  MoreHorizontal,
  RotateCcw,
  LogOut,
  Pencil,
  Check,
  ChevronLeft,
  ShieldCheck,
} from "lucide-react";
import { api, json } from "./api";
import {
  Brand,
  Empty,
  Modal,
  Spinner,
  dateLabel,
  ThemePicker,
} from "./components";
import type { Project, User } from "./types";
import { ImportProject } from "./ProjectTransfer";
import { AdminUsers } from "./AdminUsers";

const colors = ["violet", "green", "amber", "blue"];
export function ProjectForm({
  initial,
  close,
  save,
}: {
  initial?: Project;
  close: () => void;
  save: (input: {
    title: string;
    description: string;
    color: string;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState(initial?.title || ""),
    [description, setDescription] = useState(initial?.description || ""),
    [color, setColor] = useState(initial?.color || "violet"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await save({ title, description, color });
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={initial ? "Настройки проекта" : "Новый проект"} close={close}>
      <form onSubmit={submit}>
        <label>
          Название
          <input
            autoFocus
            required
            maxLength={160}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Как называется твоя следующая игра?"
          />
        </label>
        <label>
          Короткое описание
          <textarea
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="О чём игра и что хочется проверить"
          />
        </label>
        <label>Цвет проекта</label>
        <div className="color-options">
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              className={`color-choice ${c}`}
              aria-label={c}
              aria-pressed={color === c}
              onClick={() => setColor(c)}
            >
              {color === c && <Check size={16} />}
            </button>
          ))}
        </div>
        {error && (
          <div role="alert" className="error-message">
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={close}>
            Отмена
          </button>
          <button className="primary" disabled={busy || !title.trim()}>
            {busy ? <Spinner /> : initial ? "Сохранить" : "Создать проект"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
export function Projects({
  user,
  open,
  logout,
}: {
  user: User;
  open: (p: Project) => void;
  logout: () => void;
}) {
  const [projects, setProjects] = useState<Project[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [archived, setArchived] = useState(false),
    [search, setSearch] = useState(""),
    [creating, setCreating] = useState(false),
    [editing, setEditing] = useState<Project | null>(null),
    [menu, setMenu] = useState<string | null>(null),
    [admin, setAdmin] = useState(false);
  async function load() {
    try {
      setProjects(await api("/projects"));
      setError("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function archive(p: Project) {
    try {
      await api(
        `/projects/${p.id}`,
        json("PATCH", { ...p, archived: !p.archived }),
      );
      setMenu(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const visible = projects.filter(
    (p) =>
      p.archived === archived &&
      `${p.title} ${p.description}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );
  return (
    <div className="projects-page">
      <header className="dashboard-header">
        <Brand />
        <div className="account">
          <ThemePicker />
          {user.role === "admin" && (
            <button
              className="icon-button"
              aria-label="Управление пользователями"
              title="Пользователи"
              onClick={() => setAdmin(true)}
            >
              <ShieldCheck size={18} />
            </button>
          )}
          <span className="avatar">
            {user.username.slice(0, 1).toUpperCase()}
          </span>
          <span>{user.username}</span>
          <button className="icon-button" aria-label="Выйти" onClick={logout}>
            <LogOut size={17} />
          </button>
        </div>
      </header>
      <main className="dashboard">
        <div className="dashboard-intro">
          <div>
            <span className="eyebrow">ТВОЁ РАБОЧЕЕ ПРОСТРАНСТВО</span>
            <h1>
              {archived ? "Архив проектов" : "Мои проекты"}
              <span className="count">
                {projects.filter((p) => p.archived === archived).length}
              </span>
            </h1>
            <p className="muted">
              {archived
                ? "Завершённые идеи можно вернуть в работу в любой момент."
                : "Здесь начинаются новые миры, механики и эксперименты."}
            </p>
          </div>
          <div className="dashboard-actions">
            <ImportProject
              onImported={(p) => {
                setProjects((list) => [p, ...list]);
                open(p);
              }}
            />
            <button className="primary" onClick={() => setCreating(true)}>
              <Plus size={18} />
              Новый проект
            </button>
          </div>
        </div>
        <div className="project-filters">
          <div className="segmented">
            <button aria-pressed={!archived} onClick={() => setArchived(false)}>
              <FileText size={15} />В работе
            </button>
            <button aria-pressed={archived} onClick={() => setArchived(true)}>
              <Archive size={15} />
              Архив
            </button>
          </div>
          <label className="search-field">
            <Search size={17} />
            <input
              aria-label="Найти проект"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Найти проект…"
            />
          </label>
        </div>
        {error && (
          <div role="alert" className="error-message">
            {error}
            <button onClick={() => void load()}>Повторить</button>
          </div>
        )}
        {loading ? (
          <div className="loading">
            <Spinner />
          </div>
        ) : visible.length ? (
          <div className="project-grid">
            {visible.map((p) => (
              <article key={p.id} className={`project-card ${p.color}`}>
                <div className="project-card-top">
                  <span className="project-glyph">
                    <FileText size={23} strokeWidth={1.5} />
                  </span>
                  <div className="project-menu">
                    <button
                      className="icon-button"
                      aria-label={`Действия: ${p.title}`}
                      aria-expanded={menu === p.id}
                      onClick={() => setMenu(menu === p.id ? null : p.id)}
                    >
                      <MoreHorizontal size={20} />
                    </button>
                    {menu === p.id && (
                      <div className="dropdown">
                        <button
                          onClick={() => {
                            setEditing(p);
                            setMenu(null);
                          }}
                        >
                          <Pencil size={14} />
                          Изменить
                        </button>
                        <button onClick={() => void archive(p)}>
                          {p.archived ? (
                            <RotateCcw size={14} />
                          ) : (
                            <Archive size={14} />
                          )}{" "}
                          {p.archived ? "Вернуть в работу" : "В архив"}
                        </button>
                        <button onClick={() => setMenu(null)}>
                          <ChevronLeft size={14} />
                          Закрыть
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <button className="project-open" onClick={() => open(p)}>
                  <h2>{p.title}</h2>
                  <p>
                    {p.description ||
                      "Пока без описания. Здесь есть место для новой идеи."}
                  </p>
                  <span className="project-bottom">
                    <span>
                      {p.documentCount || 0} док.{" "}
                      <span className="separator">·</span>{" "}
                      {dateLabel(p.updatedAt)}
                    </span>
                    <ArrowUpRight size={18} />
                  </span>
                </button>
              </article>
            ))}
            {!archived && !search && (
              <button
                className="new-project-card"
                onClick={() => setCreating(true)}
              >
                <Plus size={25} />
                <span>Начать новый проект</span>
              </button>
            )}
          </div>
        ) : (
          <Empty
            title={
              search
                ? "Проектов не найдено"
                : archived
                  ? "Архив пока пуст"
                  : "Начнём с первой идеи"
            }
            action={
              !search && !archived ? (
                <button className="primary" onClick={() => setCreating(true)}>
                  <Plus size={17} />
                  Создать проект
                </button>
              ) : undefined
            }
          >
            {search
              ? "Попробуй другое название."
              : archived
                ? "Здесь будут проекты, которые ты отложил."
                : "Создай проект, добавь документ и опиши игру, которую хочется сделать."}
          </Empty>
        )}
        <footer className="dashboard-footer">
          <span className="status-dot" />
          Личное пространство<span className="separator">·</span>GDTool v0.1
        </footer>
      </main>
      {creating && (
        <ProjectForm
          close={() => setCreating(false)}
          save={async (input) => {
            const p = await api<Project>("/projects", json("POST", input));
            open(p);
          }}
        />
      )}
      {editing && (
        <ProjectForm
          initial={editing}
          close={() => setEditing(null)}
          save={async (input) => {
            await api(
              `/projects/${editing.id}`,
              json("PATCH", {
                ...input,
                version: editing.version,
                archived: editing.archived,
              }),
            );
            await load();
          }}
        />
      )}
      {admin && <AdminUsers current={user} close={() => setAdmin(false)} />}
    </div>
  );
}
